import React, { useCallback, useMemo, useRef } from 'react';
import { SxProp, useSx } from 'dripsy';
import { Platform } from 'react-native';

import { Slider, ISliderProps } from '../../common/Slider';
import { useColors } from '@hooks';
import { formatPlayerTimeFromMs } from '@utils';
import {
  PlayerSliderBubble,
  PlayerSliderBubbleRefType,
} from './PlayerSliderBubble';
import { TimestampType } from './TimestampText';

function findText(progress: number, timestamps: TimestampType) {
  return timestamps.find((v) => v.time <= progress);
}

const containerStyle: SxProp = {
  borderRadius: 2,
};

const sliderHeight = Platform.select({ web: 4, default: 2 });
const thumbWidth = Platform.select({ web: 12, default: 10 });
const bubbleMaxWidth = Platform.select({ web: 250, default: 180 });
const bubbleTranslateY = Platform.select({
  web: -12,
  android: 0,
  default: -16,
});

const markStyle: SxProp = {
  borderRadius: 2,
  height: Platform.select({ web: 8, default: 6 }),
  width: Platform.select({ web: 6, default: 4 }),
  bg: '$background0',
};

const panHitSlop = { top: 10, bottom: 20 };

export interface IPlayerSliderProps extends ISliderProps {
  timestamps: TimestampType;
}

export function PlayerSlider({
  timestamps,
  ...props
}: IPlayerSliderProps) {
  const sx = useSx();
  const colors = useColors();
  const bubbleRef = useRef<PlayerSliderBubbleRefType>(null);

  const renderBubble = useCallback(() => {
    const hasTimestamps = timestamps.length > 0;

    return (
      <PlayerSliderBubble
        ref={bubbleRef}
        hasTimestamps={hasTimestamps}
      />
    );
  }, [timestamps]);

  const clonedAndReversedTimestamps = useMemo(
    () =>
      timestamps
        .map((i) => ({ ...i }))
        .slice()
        .reverse(),
    [timestamps],
  );

  const setBubbleText = useCallback(
    (seconds: number) => {
      bubbleRef.current?.setText(
        formatPlayerTimeFromMs(seconds),
        findText(seconds, clonedAndReversedTimestamps)?.text,
      );
    },
    [clonedAndReversedTimestamps],
  );

  const timestampsTime = useMemo(
    () => timestamps.map((t) => t.time),
    [timestamps],
  );

  return (
    <Slider
      {...props}
      bubbleMaxWidth={bubbleMaxWidth}
      bubbleTranslateY={bubbleTranslateY}
      containerStyle={sx(containerStyle)}
      markStyle={sx(markStyle)}
      bubble={formatPlayerTimeFromMs}
      panHitSlop={panHitSlop}
      sliderHeight={sliderHeight}
      thumbWidth={thumbWidth}
      renderBubble={renderBubble}
      setBubbleText={setBubbleText}
      theme={{
        minimumTrackTintColor: colors.$primary300,
        // TODO: add opacity
        // Video component in web does not support cache
        maximumTrackTintColor: Platform.select({
          web: colors.$background0,
          default: colors.$background50,
        }),
        bubbleBackgroundColor: colors.$primary300,
        cacheTrackTintColor: colors.$background0,
      }}
      marks={timestampsTime}
    />
  );
}
